/**
 * CuttySark 賞の画面で使う純粋関数（表示用の並べ替え・残り票数・メール一覧の読み取り・
 * 投票フォームの下書きと送信内容の相互変換）。
 *
 * 票数や点数の上限・自分への投票禁止といった**判定は API 側**（domain の cuttysark-rules）が持つ。
 * ここは入力を手伝う見せ方だけを扱い、最終的な可否は API が返したメッセージで伝える。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞の参加者画面・運営画面の表示補助（並べ替え・残り票・メール一覧変換・投票下書き）
 * @graph-connects none
 */

import type {
  CuttysarkBallot,
  CuttysarkCandidate,
  CuttysarkDivision,
  CuttysarkLiveCandidate,
  CuttysarkLiveView,
  PutCuttysarkBallotRequest,
} from '@cortex/domain-product-innovation-lab-types';
import { config } from '../../../config.js';

/** 1 候補者への採点の下書き。未入力は null。 @graph-connects none */
export interface CuttysarkScoreDraft {
  achievement: number | null;
  future: number | null;
  comment: string;
}

/** 投票フォームの下書き。 @graph-connects none */
export interface CuttysarkBallotDraft {
  /** 候補者メール → 採点。 */
  scores: Record<string, CuttysarkScoreDraft>;
  /** 部門 ID → 投票先の候補者メール。 */
  votes: Record<string, string[]>;
}

/** 空の採点。 @graph-connects none */
export const EMPTY_SCORE_DRAFT: CuttysarkScoreDraft = {
  achievement: null,
  future: null,
  comment: '',
};

/**
 * 紹介に使う写真。運営が URL で登録した写真 → アップロードした写真 → プロフィール写真の順。
 * アップロードした写真は API が返す配信パス（版付き）に API のベース URL を付けて読む。
 * @graph-connects none
 */
export function candidatePhoto(
  candidate: Pick<CuttysarkLiveCandidate, 'photoUrl' | 'avatarUrl' | 'uploadedPhotoPath'>,
  apiBaseUrl: string = config.apiUrl,
): string {
  if (candidate.photoUrl !== '') return candidate.photoUrl;
  if (candidate.uploadedPhotoPath !== '') return `${apiBaseUrl}${candidate.uploadedPhotoPath}`;
  return candidate.avatarUrl;
}

/**
 * この部門で投票できる候補者（自分を除く）。
 * @graph-connects none
 */
export function eligibleCandidates<T extends Pick<CuttysarkCandidate, 'divisionId' | 'email'>>(
  candidates: readonly T[],
  divisionId: string,
  viewerEmail: string,
): T[] {
  return candidates.filter((c) => c.divisionId === divisionId && c.email !== viewerEmail);
}

/**
 * あと何票入れられるか。投票できる候補者が票数より少ない部門では、その人数を上限にする。
 * @graph-connects none
 */
export function remainingVotes(
  division: Pick<CuttysarkDivision, 'votesPerVoter'>,
  eligibleCount: number,
  selectedCount: number,
): number {
  return Math.max(Math.min(division.votesPerVoter, eligibleCount) - selectedCount, 0);
}

/**
 * 投票先の選択を切り替える。選択済みなら外し、未選択なら上限に達していない限り足す。
 * @graph-connects none
 */
export function toggleVote(selected: readonly string[], email: string, max: number): string[] {
  if (selected.includes(email)) return selected.filter((e) => e !== email);
  if (selected.length >= max) return [...selected];
  return [...selected, email];
}

/**
 * カンマ・空白・改行区切りのメール一覧を読む（小文字化・重複除去）。形式の検証は API に任せる。
 * @graph-connects none
 */
export function parseEmailList(text: string): string[] {
  const emails = text
    .split(/[\s,、;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e !== '');
  return [...new Set(emails)];
}

/**
 * 保存済みの投票をフォームの下書きに戻す。
 * @graph-connects none
 */
export function draftFromBallot(ballot: CuttysarkBallot | null): CuttysarkBallotDraft {
  if (ballot === null) return { scores: {}, votes: {} };
  return {
    scores: Object.fromEntries(
      ballot.scores.map((s) => [
        s.candidateEmail,
        { achievement: s.achievement, future: s.future, comment: s.comment },
      ]),
    ),
    votes: Object.fromEntries(ballot.votes.map((v) => [v.divisionId, [...v.candidateEmails]])),
  };
}

/**
 * 下書きを送信内容にする。何も入れていない採点は送らない。
 * @graph-connects none
 */
export function ballotRequestFromDraft(draft: CuttysarkBallotDraft): PutCuttysarkBallotRequest {
  const scores = Object.entries(draft.scores)
    .filter(([, s]) => s.achievement !== null || s.future !== null || s.comment.trim() !== '')
    .map(([candidateEmail, s]) => ({
      candidateEmail,
      achievement: s.achievement,
      future: s.future,
      comment: s.comment.trim(),
    }));
  const votes = Object.entries(draft.votes).map(([divisionId, candidateEmails]) => ({
    divisionId,
    candidateEmails,
  }));
  return { scores, votes };
}

/**
 * 採点の入力値（文字列）を点数にする。空や数値でないものは未入力扱い。
 * 範囲外の値もそのまま通し、API の検証メッセージで直してもらう。
 * @graph-connects none
 */
export function parseScoreInput(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
}

/**
 * 「Meet に参加」ボタンを出すか。URL が登録済みで、まだ終了（finished）していない回だけ。
 * 終了後の Meet に入っても意味がなく、不要な参加記録が残るため出さない。
 * @graph-connects none
 */
export function canShowJoinMeet(view: Pick<CuttysarkLiveView, 'meetUrl' | 'phase'>): boolean {
  return view.meetUrl !== '' && view.phase !== 'finished';
}
